import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { currentUserQueryOptions, readFreshCurrentUser } from '@/api/auth/auth-queries'
import { LOGIN_PATH } from '@/config/auth-routes'
import { accessFor, leafRouteId, pageFor } from '@/config/navigation'
import { HttpError } from '@/lib/http/http-error'

export class CurrentUserReadError extends Error {}

interface PageAccessInput {
  context: { queryClient: QueryClient }
  matches: readonly { routeId: string }[]
  location: { href: string }
}

export async function requirePageAccess({
  context: { queryClient },
  matches,
  location,
}: PageAccessInput): Promise<void> {
  const routeId = leafRouteId(matches)
  const page = pageFor(routeId)
  if (page.shell === 'public') return

  const user = await (
    page.permission !== null
      ? readFreshCurrentUser(queryClient)
      : queryClient.ensureQueryData({
          ...currentUserQueryOptions(),
          staleTime: 0,
          revalidateIfStale: true,
        })
  ).catch((error) => {
    if (error instanceof HttpError) throw error
    throw new CurrentUserReadError('Couldn’t reach the server.', { cause: error })
  })

  const access = accessFor(user, routeId)
  if (access.kind === 'login') {
    throw redirect({ to: LOGIN_PATH, search: { redirect: location.href } })
  }
  if (access.kind === 'elsewhere') throw redirect({ to: access.to })
}
